import { Breadcrumb, Button, Col, Dropdown, Menu, Pagination, Radio, RadioChangeEvent, Row, Select, Space, Statistic, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AuctionResponse, StartStatus } from "../../auction/types";
import { useLazySearchProductRegisterHistoryQuery } from "../../../redux/api/ContractApi";
import { AuctionRegisterHistorySearch, ListResponseFile } from "../types";
import AuctionRegisterHistoryFilter from "./components/AuctionRegisterHistoryFilter";
import { ReactComponent as ListAction } from '../../../assets/images/list_action.svg';
import { ReactComponent as ListWait } from '../../../assets/images/list_wait.svg';
import { ReactComponent as ListSuccess } from '../../../assets/images/list_success.svg';
import { ReactComponent as ListError } from '../../../assets/images/list_error.svg';
import { ReactComponent as ListUpcoming } from '../../../assets/images/list_upcoming.svg';
import { ReactComponent as ListOngoing } from '../../../assets/images/list_ongoing.svg';
import { contractTypes } from "../../common/types/types";
import ProductDetailModal from "../../common/components/ProductDetailModal";
import AddProductModal from "../../auction/register/components/AddProductModal";
import { useAppSelector } from "../../../redux/Hooks";
import ConfirmModal from "../../common/components/ConfirmModal";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { exportFile } from "../../common/ts/CommonFunctions";

const { Option } = Select;

interface Products {
    code: string,
    name: string,
    startPrice: number,
    category: string,
    id: number,
    statuteId: number,
    startStatus?: number,
}

interface AuctionRegisterHistoryTable {
    key: number,
    products: Products[],
    status: number,
    createdDate: string,
    files: ListResponseFile[],
}

const contractStatusIcon: Record<number, { icon: React.ReactNode, title: string }> = {
    [contractTypes.NOTSUBMIT]: { icon: <div style={{ display: 'inline-block', height: 14, width: 14, marginRight: 5 }}><ListWait /></div>, title: "Chưa tiếp nhận" },
    [contractTypes.PROCESSING]: { icon: <div style={{ display: 'inline-block', height: 14, width: 14, marginRight: 5 }}><ListWait /></div>, title: "Đang xử lý" },
    [contractTypes.DENY]: { icon: <div style={{ display: 'inline-block', height: 14, width: 14, marginRight: 5 }}><ListError /></div>, title: "Không ký hợp đồng" },
    [contractTypes.SIGNED]: { icon: <div style={{ display: 'inline-block', height: 14, width: 14, marginRight: 5 }}><ListSuccess /></div>, title: "Đã ký hợp đồng" },
}

const productStatusIcon: Record<number, { icon: React.ReactNode, title: string }> = {
    [StartStatus.UPCOMING]: { icon: <ListUpcoming />, title: "Sắp diễn ra" },
    [StartStatus.ONGOING]: { icon: <ListOngoing />, title: "Đang diễn ra" },
    [StartStatus.FINISH]: { icon: <ListSuccess />, title: "Đã kết thúc" },
    [StartStatus.PAUSE]: { icon: <ListError />, title: "Đã tạm dừng" },
}

const AuctionRegisterHistory = () => {
    const divRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [submitValue, setSubmitValue] = useState<AuctionRegisterHistorySearch>();
    const [tableData, setTableData] = useState<AuctionRegisterHistoryTable[]>([]);
    const [statusContract, setStatusContract] = useState(contractTypes.NOTSUBMIT);
    const [productId, setProductId] = useState(0);
    const [statuteId, setStatuteId] = useState(0);
    const [visible, setVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [cancelVisible, setCancelVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

    const userState = useAppSelector(state => state.login);

    const [trigger, response] = useLazySearchProductRegisterHistoryQuery();

    useEffect(() => {
        trigger({
            page: 0,
            size: 10,
            statusContracts: [contractTypes.NOTSUBMIT, contractTypes.DENY, contractTypes.PROCESSING],
        })
    }, [trigger])

    useEffect(() => {
        let newList: AuctionRegisterHistoryTable[] = [];
        if (response.data?.content && response.isSuccess) {
            response.data.content.forEach(item => {
                newList.push({
                    key: item.contractId,
                    products: item.products.map(product => ({
                        name: product.productName || "-",
                        code: product.productCode || "-",
                        startPrice: product.startPrice || 0,
                        category: product.categories?.[0]?.name || "",
                        id: product.productId,
                        statuteId: product.statute?.statuteId || 0,
                        ...product.startStatus && product.productCode && { startStatus: product.startStatus },
                    })),
                    status: item.status || 0,
                    createdDate: item.createdDate ? format(item.createdDate, "dd/MM/yyyy HH:mm") : "",
                    files: item.fileHopDong || [],
                })
            })
        }
        setTableData(newList);
        scrollToStart();
    }, [response])

    const triggerSearch = (currentSubmitValue?: AuctionRegisterHistorySearch) => {
        // statusContract === 0 ? trigger({
        //     page: page - 1,
        //     size,
        //     ...(currentSubmitValue || submitValue),
        //     statusContracts: [contractTypes.NOTSUBMIT, contractTypes.DENY, contractTypes.PROCESSING]
        // })
        // : trigger({
        //     page: page - 1,
        //     size,
        //     ...(currentSubmitValue || submitValue),
        //     statusContract: contractTypes.SIGNED
        // })
        trigger({
            page: page - 1,
            size,
            ...(currentSubmitValue || submitValue),
        })
    }

    const scrollToStart = () => {
        if (divRef.current) {
            divRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }
    const auctionRegisterHistoryColumns: ColumnsType<AuctionRegisterHistoryTable> = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 70,
            align: 'center',
            render: (value, record, index) => {
                return (<span
                    style={{ border: '1px solid black', padding: '7px 13px', borderRadius: '20px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    key={index}>
                    {(page - 1) * size + index + 1}
                </span>)
            }
        },
        {
            title: 'ID đăng ký',
            dataIndex: 'key',
            key: 'key',
            width: 90,
        },
        {
            title: "Thời gian đăng ký bán tài sản",
            dataIndex: "createdDate",
            key: 'createdDate',
            width: 110,
        },
        {
            title: "Trạng thái đấu giá",
            dataIndex: "startStatus",
            key: "startStatus",
            width: 125,
            render: (value, record) => {
                let itemArray = record.products;
                if (record.products?.length > 2 && !selectedRowKeys.includes(record.key)) {
                    itemArray = record.products.slice(0, 2);
                }
                return (
                    <>
                        {itemArray.map(item => {
                            if (item.startStatus) {
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center' }} key={item.id}>
                                        {productStatusIcon[item.startStatus]?.icon || <></>}<span style={{ marginLeft: 5 }}>{productStatusIcon[item.startStatus]?.title || ""}</span>
                                    </div>)
                            }
                            return (<div>-</div>)
                        })}
                    </>)
            }
        },
        // {
        //     key: 'action',
        //     width: 30,
        //     render: (value, record, index) => {
        //         if (record.products?.length > 2) {
        //             if (selectedRowKeys.includes(record.key)) {
        //                 return <UpOutlined onClick={() => onRowClick(record.key)} />
        //             }

        //             return <DownOutlined onClick={() => onRowClick(record.key)} />
        //         }

        //         return <></>
        //     }
        // },
        {
            title: "Tên tài sản",
            dataIndex: "products",
            key: "productName",
            width: 110,
            ellipsis: true,
            render: (value: Products[], record) => {
                let itemArray = value;
                if (value.length > 2 && !selectedRowKeys.includes(record.key)) {
                    itemArray = value.slice(0, 2);
                }
                return (<div style={{ display: "flex" }}>
                    <div style={{ display: "flex", alignItems: "center", paddingRight: 10 }}>
                        {record.products?.length > 2 ? (
                            selectedRowKeys.includes(record.key) ?
                                <UpOutlined onClick={() => onRowClick(record.key)} /> :
                                <DownOutlined onClick={() => onRowClick(record.key)} />) : <></>}
                    </div>
                    <div>
                        {itemArray.map((item, index) => {
                            return (
                                <div title={item.name} className="anchor-text text-overflow-custom-one-line" key={index} style={{ color: '#40a9ff', maxWidth: 120 }} onClick={() => openDetailModal(item.id, item.statuteId)}>{item.name}</div>
                            )
                        })}
                    </div>
                </div>)
            }
        },
        {
            title: "Mã tài sản",
            dataIndex: "products",
            key: "productCode",
            render: (value: Products[], record) => {
                let itemArray = value;
                if (value.length > 2 && !selectedRowKeys.includes(record.key)) {
                    itemArray = value.slice(0, 2);
                }
                return (
                    <>
                        {itemArray.map((item, index) => {
                            return (
                                <div key={index}>{item.code}</div>
                            )
                        })}
                    </>
                )
            }
        },
        {
            title: "Nhóm tài sản",
            dataIndex: "products",
            key: 'categories',
            width: 130,
            ellipsis: true,
            render: (value: Products[], record) => {
                let itemArray = value;
                if (value.length > 2 && !selectedRowKeys.includes(record.key)) {
                    itemArray = value.slice(0, 2);
                }
                return (<>
                    {itemArray.map((item, index) => {
                        return (
                            <div title={item.category} className="text-overflow-custom-one-line" key={index}>{item.category}</div>
                        )
                    })}
                </>)
            }
        },
        {
            title: "Giá khởi điểm (VNĐ)",
            dataIndex: "products",
            key: 'startPrice',
            width: 110,
            render: (value: Products[], record) => {
                let itemArray = value;
                if (value.length > 2 && !selectedRowKeys.includes(record.key)) {
                    itemArray = value.slice(0, 2);
                }
                return (<>
                    {itemArray.map((item, index) => {
                        return (
                            <Statistic
                                key={index}
                                value={item.startPrice}
                                groupSeparator='.'
                                decimalSeparator=","
                                precision={0}
                                valueStyle={{ fontFamily: 'Quicksand', fontSize: 14, fontWeight: 'bold' }} />
                        )
                    })}
                </>)
            }
        },
        {
            title: 'Trạng thái hợp đồng',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (value: number) => {
                return (<div style={{ display: 'flex', alignItems: 'center' }}>
                    {contractStatusIcon[value]?.icon || <></>}<span style={{ marginLeft: 5 }}>{contractStatusIcon[value]?.title || ""}</span>
                </div>)
            }
        },
        {
            title: 'File hợp đồng',
            dataIndex: 'files',
            key: 'file',
            // ellipsis: true,
            render: (value: ListResponseFile[]) => {
                if (value?.length === 0) {
                    return <></>
                }
                return (<div className="anchor-text text-ellipsis" style={{ color: '#40a9ff' }} onClick={() => downloadFile(value[0].fileUrl, value[0].fileName)}>{value[0].fileName}</div>)
            }
        },
    ]

    const onPageSizeChange = (currentSize: number) => {
        setSize(currentSize);
        setPage(1);
        triggerSearch({ ...submitValue, page: 0, size: currentSize })
    }

    const onPageChange = (currentPage: number) => {
        setPage(currentPage);
        triggerSearch({ ...submitValue, page: currentPage - 1 })
    }

    const onTypeChange = (e: RadioChangeEvent) => {
        setStatusContract(e.target.value);
        setPage(1);
        // console.log('aaaaaaaaaa')
        if (e.target.value === 1) {
            triggerSearch({ ...submitValue, statusContract: contractTypes.SIGNED, page: 0 });
        } else {
            triggerSearch({ ...submitValue, statusContracts: [contractTypes.NOTSUBMIT, contractTypes.DENY, contractTypes.PROCESSING], page: 0 });
        }
    }

    const openDetailModal = (id: number, statuteId: number) => {
        setProductId(id);
        setStatuteId(statuteId);
        toggleDetailModal();
    }

    const toggleDetailModal = () => {
        setVisible(!visible);
    }

    const toggleEditModal = () => {
        if (editVisible) {
            triggerSearch();
        }
        setEditVisible(!editVisible);
    }

    const toggleCancelModal = () => {
        setCancelVisible(!cancelVisible);
    }

    const onRowClick = (key: number) => {
        let keyIndex = selectedRowKeys.indexOf(key);
        let newSelectedRowKeys = [...selectedRowKeys];
        if (keyIndex !== -1) {
            newSelectedRowKeys.splice(keyIndex, 1);
        } else {
            newSelectedRowKeys.push(key);
        }
        setSelectedRowKeys(newSelectedRowKeys);
    }

    const downloadFile = (url: string, fileName: string) => {
        exportFile(url, fileName);
    }

    const exportExcel = () => {
        let filename = `${format(new Date(), "dd-MM-yyyy")}_Lịch sử bán tài sản ` + (statusContract ? "đã ký" : "chưa ký") + `.xlsx`;
        let url = process.env.REACT_APP_API_URL + `/contract/search-history/export?page=0&properties=modifiedDate&size=10000&sortBy=DESC`;

        if (submitValue && Object.keys(submitValue).length) {
            Object.entries(submitValue).forEach((item) => {
                if (item[1]) {
                    url += `&${item[0]}=${item[1]}`;
                }
            })
        }

        if (statusContract === 0) {
            url += `&statusContracts=${contractTypes.NOTSUBMIT}&statusContracts=${contractTypes.DENY}&statusContracts=${contractTypes.PROCESSING}`;
        } else if (statusContract === 1) {
            url += `&statusContract=${contractTypes.SIGNED}`;
        }

        exportFile(url, filename);
    }

    return (
        <div
            ref={divRef}
            style={{
                // padding: '40px calc((100vw - 1440px) / 2) 40px',
                background: "#ececec",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '100%',
                paddingTop: 40,
                paddingBottom: 40
            }}
        >
            <Space style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }} className="responsive-padding-1440">
                <Breadcrumb separator="  " className="custom-breadcrumb">
                    <Breadcrumb.Item>
                        <Link to="/">
                            Trang chủ
                        </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        Lịch sử bán tài sản
                    </Breadcrumb.Item>
                </Breadcrumb>

                <div className="responsive-title">Lịch sử bán tài sản</div>
            </Space>

            <div className="responsive-padding-1440">
                <Row gutter={30}>
                    <Col span={6}>
                        {/* <AuctionHistoryFilter setSubmitValue={setSubmitValue} /> */}
                        <AuctionRegisterHistoryFilter setSubmitValue={setSubmitValue} triggerSearch={triggerSearch} statusContract={statusContract} />
                    </Col>
                    <Col span={18}>
                        <Row justify="space-between" className="product-list-filter">
                            <Radio.Group value={statusContract} onChange={onTypeChange} className="custom-radio-group">
                                <Radio.Button value={0}>Chưa ký hợp đồng</Radio.Button>
                                <Radio.Button value={1}>Đã ký hợp đồng</Radio.Button>
                            </Radio.Group>
                            <Button
                                onClick={exportExcel}
                                className="button-light-yellow"
                                style={{ padding: '11px 22px', display: 'flex', alignItems: 'center' }}>Xuất dữ liệu</Button>
                        </Row>

                        <Row justify="space-between" align="middle" style={{ marginTop: 15, whiteSpace: 'pre-wrap' }}>
                            <span>Tổng số <strong>{response.data?.totalElements || 0}</strong> kết quả</span>
                        </Row>

                        <div className='custom-table-wrapper'>
                            <Table
                                scroll={{ x: !statusContract ? 'max-content' : '1300px' }}
                                columns={!statusContract ?
                                    auctionRegisterHistoryColumns.filter(item => item.key !== "productCode" && item.key !== "file" && item.key !== "startStatus")
                                    : auctionRegisterHistoryColumns}
                                dataSource={tableData}
                                pagination={false}
                                rowClassName={(record) => {
                                    let className = "";
                                    if (selectedRowKeys.includes(record.key) && record.products.length > 2) {
                                        className = "custom-table-selected-row"
                                    }
                                    return className;
                                }}
                            />
                        </div>

                        <Row justify="end" align="middle" style={{ marginTop: 35 }}>
                            <Select className="left-arrow-select" defaultValue={10} value={size} onChange={onPageSizeChange} dropdownStyle={{ borderRadius: 15 }}>
                                <Option value={10}>10</Option>
                                <Option value={20}>20</Option>
                                <Option value={50}>50</Option>
                                <Option value={100}>100</Option>
                            </Select>
                            <div style={{ marginRight: 20, marginLeft: 8, fontSize: 14 }}>Bản ghi</div>
                            <Pagination
                                defaultCurrent={1}
                                total={response.data?.totalElements || 0}
                                defaultPageSize={10}
                                pageSize={size}
                                className="custom-pagination"
                                current={page}
                                onChange={onPageChange}
                                showSizeChanger={false}
                            />
                        </Row>
                    </Col>
                </Row>
            </div>

            {visible ?
                <ProductDetailModal
                    visible={visible}
                    toggleModal={toggleDetailModal}
                    id={productId}
                    toggleEdit={toggleEditModal}
                    triggerSearch={statusContract ? () => triggerSearch({ statusContract: contractTypes.SIGNED })
                        : () => triggerSearch({
                            statusContracts: [contractTypes.NOTSUBMIT, contractTypes.DENY,
                            contractTypes.PROCESSING]
                        })}
                />
                : <></>}

            {editVisible ?
                <AddProductModal
                    visible={editVisible}
                    toggleModal={toggleEditModal}
                    editId={productId}
                    statuteId={statuteId}
                    setStatuteId={setStatuteId}
                    sellerId={userState.user.userId || 0}
                    setEditId={setProductId}
                    toggleCancel={toggleCancelModal} />
                : <></>}

            {cancelVisible ?
                <ConfirmModal
                    visible={cancelVisible}
                    status="error"
                    onOk={() => {
                        toggleEditModal();
                        toggleCancelModal()
                    }}
                    onCancel={toggleCancelModal}
                    title={<div style={{ textAlign: 'center' }}>
                        <div className="font-size-16">Bạn có chắc chắn muốn thoát và không lưu dữ liệu vừa nhập</div>
                    </div>}
                /> : <></>}
        </div>
    )
}

export default AuctionRegisterHistory;